/* ==========================================================================
   whero-notify-search.js — /pages/notify-me flavor search → restock subscribe
   --------------------------------------------------------------------------
   Type-ahead over EVERY flavor we've made:
     - current menu  → seeded from the [data-whero-ns-catalog] JSON the
                       section renders server-side (onMenu: true)
     - the vault     → fetched + paginated from /apps/api/vote/archive
                       (onMenu: false), merged + de-duped by slug. Current
                       menu wins a slug collision, so a flavor that just
                       came back reads as "On the menu now".

   Multi-select: pick any number of flavors, enter email once, subscribe to
   all of them on submit. Subscription reuses the Klaviyo client-side path
   exposed by whero-notify-me.js (window.wheroNotifyMe): ONE add-to-list
   call, then ONE "Subscribed to Restock Alert" event per picked flavor
   (source: "notify-search"). Slugs are remembered in the shared
   whero_restock_subs localStorage so repeat visitors aren't re-prompted.

   Degrades gracefully: vault 404/unreachable → current-menu-only search;
   Klaviyo unconfigured → the section already rendered a mailto fallback
   instead of this form, so this file simply finds nothing to wire.
   ========================================================================== */
(function () {
  'use strict';

  var MAX_RESULTS = 8;
  var ARCHIVE_MAX = 500;
  var SEARCH_DEBOUNCE_MS = 120;

  var sections = document.querySelectorAll('[data-whero-notify-search]');
  for (var i = 0; i < sections.length; i++) initSection(sections[i]);

  function initSection(section) {
    var input = section.querySelector('[data-whero-ns-input]');
    var results = section.querySelector('[data-whero-ns-results]');
    var searchStatus = section.querySelector('[data-whero-ns-searchstatus]');
    var tray = section.querySelector('[data-whero-ns-tray]');
    var chips = section.querySelector('[data-whero-ns-chips]');
    var form = section.querySelector('[data-whero-ns-form]');
    var emailInput = section.querySelector('[data-whero-ns-email]');
    var phoneInput = section.querySelector('[data-whero-ns-phone]');
    var consentInput = section.querySelector('[data-whero-ns-consent]');
    var submitBtn = section.querySelector('[data-whero-ns-submit]');
    var errorEl = section.querySelector('[data-whero-ns-error]');
    var successEl = section.querySelector('[data-whero-ns-success]');
    var successBody = section.querySelector('[data-whero-ns-success-body]');
    var catalogEl = section.querySelector('[data-whero-ns-catalog]');
    var shopSelect = section.querySelector('[data-whero-ns-shop]');

    // Form absent → Klaviyo unconfigured (mailto fallback rendered). Nothing to do.
    if (!form || !input || !results) return;

    // Default the shop picker to the visitor's nearest / last-picked shop
    // (the current-flavors selector saves it to localStorage). Falls back
    // to the server-rendered "Either location" default when unknown.
    if (shopSelect) {
      try {
        var savedShop = localStorage.getItem('wynstons.selectedShop') || '';
        if (savedShop) {
          for (var oi = 0; oi < shopSelect.options.length; oi++) {
            if (shopSelect.options[oi].value === savedShop) { shopSelect.value = savedShop; break; }
          }
        }
      } catch (_) { /* storage blocked — keep the server default */ }
    }

    var ARCHIVE_ENDPOINT = section.getAttribute('data-archive-endpoint') || '/apps/api/vote/archive';
    var TAG_ON_MENU = section.getAttribute('data-tag-on-menu') || 'On the menu now';
    var TAG_IN_VAULT = section.getAttribute('data-tag-in-vault') || 'In the vault';
    var EMPTY_COPY = section.getAttribute('data-empty-copy') || 'No match yet — keep typing.';
    var SUCCESS_TPL = section.getAttribute('data-success-tpl') || "We'll reach out the day {flavors} back in the case.";
    var ERROR_COPY = section.getAttribute('data-error-copy') || "Couldn't save that — try again in a sec?";
    var sectionId = section.getAttribute('data-section-id') || '0';

    /* ---- data model --------------------------------------------------- */
    var bySlug = {};        // slug -> flavor record
    var index = [];         // searchable array (rebuilt as data lands)
    var selected = {};      // slug -> flavor (the picks)
    var selectedOrder = []; // preserve pick order for the success line
    var currentResults = [];
    var activeIndex = -1;   // keyboard highlight within currentResults

    // Seed current-menu flavors from the server-rendered bundle.
    var catalog = [];
    if (catalogEl) {
      try { catalog = JSON.parse(catalogEl.textContent) || []; } catch (_) { catalog = []; }
    }
    addFlavors(catalog);
    rebuildIndex();

    // Pull the vault (archive) and merge when it resolves.
    fetchArchive(ARCHIVE_ENDPOINT, ARCHIVE_MAX).then(function (list) {
      if (!list || !list.length) return;
      addFlavors(list.map(function (f) {
        return {
          slug: f.slug || handleize(f.name),
          name: f.name,
          category: f.category || '',
          imageUrl: f.image_url || '',
          description: f.description || '',
          onMenu: false
        };
      }));
      rebuildIndex();
      // If the visitor is mid-search, refresh the open list with new hits.
      if (document.activeElement === input && input.value.trim()) runSearch(input.value);
    }).catch(function () { /* vault unreachable — current-menu search still works */ });

    function addFlavors(arr) {
      for (var j = 0; j < arr.length; j++) {
        var f = arr[j];
        if (!f || !f.name) continue;
        var slug = f.slug || handleize(f.name);
        if (!slug) continue;
        if (bySlug[slug]) {
          // Collision: let the current-menu record win the status tag.
          if (f.onMenu && !bySlug[slug].onMenu) bySlug[slug].onMenu = true;
          continue;
        }
        bySlug[slug] = {
          slug: slug,
          name: String(f.name),
          category: f.category || '',
          imageUrl: f.imageUrl || f.image_url || '',
          description: f.description || '',
          onMenu: !!f.onMenu
        };
      }
    }

    function rebuildIndex() {
      index = [];
      for (var s in bySlug) {
        if (!Object.prototype.hasOwnProperty.call(bySlug, s)) continue;
        var f = bySlug[s];
        f._norm = normalize(f.name);
        index.push(f);
      }
    }

    /* ---- search ------------------------------------------------------- */
    function search(q) {
      var nq = normalize(q);
      if (!nq) return [];
      var starts = [], contains = [];
      for (var k = 0; k < index.length; k++) {
        var f = index[k];
        var pos = f._norm.indexOf(nq);
        if (pos === 0) starts.push(f);
        else if (pos > 0) contains.push(f);
      }
      starts.sort(cmp);
      contains.sort(cmp);
      return starts.concat(contains).slice(0, MAX_RESULTS);
      function cmp(a, b) {
        if (a.onMenu !== b.onMenu) return a.onMenu ? -1 : 1; // in-case first
        return a.name.localeCompare(b.name);
      }
    }

    var debounceTimer = null;
    function runSearchDebounced(q) {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () { runSearch(q); }, SEARCH_DEBOUNCE_MS);
    }

    function runSearch(q) {
      currentResults = search(q);
      activeIndex = -1;
      renderResults(currentResults, q.trim().length > 0);
    }

    /* ---- results rendering -------------------------------------------- */
    function renderResults(list, hasQuery) {
      results.innerHTML = '';

      if (!hasQuery) {
        closeResults();
        setSearchStatus('');
        return;
      }
      if (!list.length) {
        closeResults();
        setSearchStatus(EMPTY_COPY);
        return;
      }
      setSearchStatus('');

      for (var n = 0; n < list.length; n++) {
        results.appendChild(buildOption(list[n], n));
      }
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    function buildOption(flavor, idx) {
      var li = document.createElement('li');
      li.className = 'whero-notify-search__option';
      li.id = 'whero-ns-opt-' + sectionId + '-' + idx;
      li.setAttribute('role', 'option');
      li.setAttribute('data-slug', flavor.slug);
      var isPicked = !!selected[flavor.slug];
      li.setAttribute('aria-selected', isPicked ? 'true' : 'false');
      if (isPicked) li.classList.add('is-picked');

      // Thumb (image or initial placeholder)
      var thumb = document.createElement('span');
      thumb.className = 'whero-notify-search__opt-thumb';
      var url = typeof flavor.imageUrl === 'string' ? flavor.imageUrl.trim() : '';
      if (url && url.indexOf('https://') === 0) {
        var img = document.createElement('img');
        img.src = url;
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        thumb.appendChild(img);
      } else {
        thumb.classList.add('whero-notify-search__opt-thumb--placeholder');
        thumb.textContent = (flavor.name.charAt(0) || '').toUpperCase();
      }
      li.appendChild(thumb);

      // Body: name + status tag
      var body = document.createElement('span');
      body.className = 'whero-notify-search__opt-body';

      var nameEl = document.createElement('span');
      nameEl.className = 'whero-notify-search__opt-name';
      nameEl.textContent = flavor.name;
      body.appendChild(nameEl);

      var tag = document.createElement('span');
      tag.className = 'whero-notify-search__opt-tag ' +
        (flavor.onMenu ? 'whero-notify-search__opt-tag--menu' : 'whero-notify-search__opt-tag--vault');
      tag.textContent = flavor.onMenu ? TAG_ON_MENU : TAG_IN_VAULT;
      body.appendChild(tag);

      if (alreadySubscribed(flavor.slug) && !isPicked) {
        var seen = document.createElement('span');
        seen.className = 'whero-notify-search__opt-seen';
        seen.textContent = '✓ already on it';
        body.appendChild(seen);
      }
      li.appendChild(body);

      // Add / picked indicator
      var pip = document.createElement('span');
      pip.className = 'whero-notify-search__opt-pip';
      pip.setAttribute('aria-hidden', 'true');
      pip.textContent = isPicked ? '✓' : '+';
      li.appendChild(pip);

      return li;
    }

    function closeResults() {
      results.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    }

    function setSearchStatus(msg) {
      if (!searchStatus) return;
      if (!msg) { searchStatus.hidden = true; searchStatus.textContent = ''; return; }
      searchStatus.textContent = msg;
      searchStatus.hidden = false;
    }

    /* ---- selection ---------------------------------------------------- */
    function toggleSelect(slug) {
      var f = bySlug[slug];
      if (!f) return;
      if (selected[slug]) {
        delete selected[slug];
        selectedOrder = selectedOrder.filter(function (s) { return s !== slug; });
      } else {
        selected[slug] = f;
        selectedOrder.push(slug);
      }
      renderChips();
      updateSubmitState();
      // Reflect the change in any visible result row for this slug.
      var row = results.querySelector('[data-slug="' + cssEscape(slug) + '"]');
      if (row) {
        var picked = !!selected[slug];
        row.setAttribute('aria-selected', picked ? 'true' : 'false');
        row.classList.toggle('is-picked', picked);
        var pip = row.querySelector('.whero-notify-search__opt-pip');
        if (pip) pip.textContent = picked ? '✓' : '+';
      }
    }

    function renderChips() {
      if (!chips || !tray) return;
      chips.innerHTML = '';
      var keys = selectedOrder.slice();
      if (!keys.length) { tray.hidden = true; return; }
      tray.hidden = false;
      for (var c = 0; c < keys.length; c++) {
        var f = selected[keys[c]];
        if (!f) continue;
        var li = document.createElement('li');
        li.className = 'whero-notify-search__chip';

        var label = document.createElement('span');
        label.className = 'whero-notify-search__chip-label';
        label.textContent = f.name;
        li.appendChild(label);

        var rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'whero-notify-search__chip-remove';
        rm.setAttribute('aria-label', 'Remove ' + f.name);
        rm.setAttribute('data-slug', f.slug);
        rm.textContent = '×';
        li.appendChild(rm);

        chips.appendChild(li);
      }
    }

    function updateSubmitState() {
      if (!submitBtn) return;
      submitBtn.disabled = selectedOrder.length === 0;
    }

    /* ---- events ------------------------------------------------------- */
    input.addEventListener('input', function () { runSearchDebounced(input.value); });

    input.addEventListener('focus', function () {
      if (input.value.trim() && currentResults.length) {
        results.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      }
    });

    input.addEventListener('keydown', function (e) {
      if (results.hidden || !currentResults.length) {
        if (e.key === 'ArrowDown' && input.value.trim()) { runSearch(input.value); }
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        activeIndex = (activeIndex + 1) % currentResults.length;
        highlightActive();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        activeIndex = (activeIndex - 1 + currentResults.length) % currentResults.length;
        highlightActive();
      } else if (e.key === 'Enter') {
        if (activeIndex >= 0 && currentResults[activeIndex]) {
          e.preventDefault();
          toggleSelect(currentResults[activeIndex].slug);
        }
      } else if (e.key === 'Escape') {
        closeResults();
      }
    });

    function highlightActive() {
      var rows = results.querySelectorAll('.whero-notify-search__option');
      for (var r = 0; r < rows.length; r++) rows[r].classList.toggle('is-active', r === activeIndex);
      if (activeIndex >= 0 && rows[activeIndex]) {
        input.setAttribute('aria-activedescendant', rows[activeIndex].id);
        rows[activeIndex].scrollIntoView({ block: 'nearest' });
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    }

    // mousedown (not click) so the input doesn't blur-and-close before we
    // toggle; preventDefault keeps focus in the search box.
    results.addEventListener('mousedown', function (e) {
      var opt = e.target.closest && e.target.closest('.whero-notify-search__option');
      if (!opt) return;
      e.preventDefault();
      var slug = opt.getAttribute('data-slug');
      if (slug) toggleSelect(slug);
    });

    // Close the list when focus leaves the combo (allow option mousedown first).
    document.addEventListener('click', function (e) {
      if (results.hidden) return;
      var within = e.target.closest && e.target.closest('.whero-notify-search__searchrow');
      if (!within) closeResults();
    });

    if (chips) {
      chips.addEventListener('click', function (e) {
        var rm = e.target.closest && e.target.closest('.whero-notify-search__chip-remove');
        if (!rm) return;
        var slug = rm.getAttribute('data-slug');
        if (slug) toggleSelect(slug);
      });
    }

    /* ---- submit ------------------------------------------------------- */
    var submitting = false;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (submitting) return;
      hideError();

      if (!selectedOrder.length) { showError('Pick at least one flavor first.'); return; }

      var email = emailInput ? emailInput.value.trim() : '';
      if (!emailLooksValid(email)) {
        if (emailInput) { emailInput.setAttribute('aria-invalid', 'true'); emailInput.focus(); }
        showError('That email looks off — mind double-checking?');
        return;
      }
      if (emailInput) emailInput.removeAttribute('aria-invalid');

      var phone = phoneInput ? phoneInput.value.trim() : '';
      var smsConsent = !!(consentInput && consentInput.checked);
      if (phone && !phoneLooksValid(phone)) {
        if (phoneInput) { phoneInput.setAttribute('aria-invalid', 'true'); phoneInput.focus(); }
        showError('Phone number needs at least 10 digits.');
        return;
      }
      if (phoneInput) phoneInput.removeAttribute('aria-invalid');
      var sendPhone = (phone && smsConsent) ? phone : '';

      var api = window.wheroNotifyMe;
      if (!api || !api.isConfigured || !api.isConfigured()) {
        showError(ERROR_COPY);
        return;
      }

      submitting = true;
      if (submitBtn) { submitBtn.disabled = true; submitBtn.setAttribute('aria-busy', 'true'); }

      var picks = selectedOrder.map(function (s) { return selected[s]; }).filter(Boolean);
      // Shop preference applies to every flavor in this submission. Empty
      // string ("Either location") → the bridge treats it as "any".
      var selectedShop = shopSelect ? shopSelect.value : '';

      // One add-to-list call, then one event per flavor (the durable
      // per-flavor record the Klaviyo segments query).
      api.subscribe(email, sendPhone, smsConsent, {}).then(function (res) {
        if (!res.ok && res.status !== 202) throw new Error('HTTP ' + res.status);
        var events = picks.map(function (f) {
          if (api.remember) api.remember(f.slug);
          return api.trackEvent(email, sendPhone, 'Subscribed to Restock Alert', {
            flavor_slug: f.slug,
            flavor_name: f.name,
            source: 'notify-search',
            shop: selectedShop
          }).catch(function () { /* per-event tracking failures are silent */ });
        });
        return Promise.all(events);
      }).then(function () {
        renderSuccess(picks);
      }).catch(function () {
        showError(ERROR_COPY);
      }).then(function () {
        submitting = false;
        if (submitBtn) { submitBtn.disabled = selectedOrder.length === 0; submitBtn.removeAttribute('aria-busy'); }
      });
    });

    function renderSuccess(picks) {
      var names = picks.map(function (f) { return f.name; });
      if (successBody) {
        var verbList = joinNames(names) + (names.length === 1 ? ' is' : ' are');
        successBody.textContent = SUCCESS_TPL.indexOf('{flavors}') !== -1
          ? SUCCESS_TPL.replace('{flavors}', verbList)
          : SUCCESS_TPL;
      }
      form.hidden = true;
      if (successEl) {
        successEl.hidden = false;
        try { successEl.focus(); } catch (_) {}
      }
    }

    /* ---- error UI ----------------------------------------------------- */
    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg || ERROR_COPY;
      errorEl.hidden = false;
    }
    function hideError() {
      if (!errorEl) return;
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    /* ---- shared helpers ----------------------------------------------- */
    function alreadySubscribed(slug) {
      var api = window.wheroNotifyMe;
      return !!(api && api.hasSubscribed && api.hasSubscribed(slug));
    }
  }

  /* ====================================================================== */
  /* module-level helpers (no per-section state)                            */
  /* ====================================================================== */

  function fetchArchive(endpoint, cap) {
    var all = [];
    function page(cursor) {
      var url = endpoint;
      if (cursor) url += (url.indexOf('?') === -1 ? '?' : '&') + 'cursor=' + encodeURIComponent(cursor);
      return fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin'
      }).then(function (res) {
        if (res.status === 404) return { done: true };          // not deployed → empty
        if (!res.ok) throw new Error('archive_http_' + res.status);
        return res.json().catch(function () { throw new Error('archive_bad_json'); });
      }).then(function (body) {
        if (body && body.done) return all;
        if (!body || !Array.isArray(body.flavors)) throw new Error('archive_bad_shape');
        for (var i = 0; i < body.flavors.length && all.length < cap; i++) all.push(body.flavors[i]);
        var next = body.cursor;
        if (next && all.length < cap) return page(next);
        return all;
      });
    }
    return page(null);
  }

  function normalize(s) {
    s = String(s == null ? '' : s).toLowerCase();
    // Strip diacritics (combining marks U+0300–U+036F) so "crème" matches "creme".
    if (s.normalize) s = s.normalize('NFD').replace(/[̀-ͯ]/g, '');
    return s.replace(/['’]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
  }

  // Mirror Shopify's handleize closely enough for slug fallback (apostrophes
  // stripped, not hyphenated) so slugs match the notify-me snippet's.
  function handleize(name) {
    return String(name == null ? '' : name).toLowerCase()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function emailLooksValid(s) {
    return typeof s === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
  }

  function phoneLooksValid(s) {
    if (!s) return true;
    return s.replace(/[^\d]/g, '').length >= 10;
  }

  function joinNames(names) {
    if (!names.length) return '';
    if (names.length === 1) return names[0];
    if (names.length === 2) return names[0] + ' and ' + names[1];
    return names.slice(0, -1).join(', ') + ', and ' + names[names.length - 1];
  }

  // Minimal CSS.escape fallback for attribute selectors (quotes/specials in
  // slugs are unlikely post-handleize, but be safe).
  function cssEscape(s) {
    if (window.CSS && window.CSS.escape) return window.CSS.escape(s);
    return String(s).replace(/["\\\]]/g, '\\$&');
  }
})();
